"use client";

import * as React from "react";
import { Loader2, Plus, X } from "lucide-react";

import { cn } from "@/modern/utils";
import { Badge } from "@/modern/components/ui/badge";
import { Skeleton } from "@/modern/components/ui/skeleton";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/modern/components/ui/command";
import { Command as CommandPrimitive } from "cmdk";
import { Popover, PopoverAnchor, PopoverContent } from "@/modern/components/ui/popover";

export interface TagInputItem {
  value: string;
  label: string;
}

export interface TagInputProps {
  items: TagInputItem[];
  value: string[];
  onValueChange: (values: string[]) => void;
  onCreateOption?: (label: string) => void;
  isCreating?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function TagInput({ items, value, onValueChange, onCreateOption, isCreating = false, placeholder = "Add tag...", disabled = false, className }: TagInputProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [duplicateError, setDuplicateError] = React.useState(false);

  const selectedItems = React.useMemo(() => items.filter((item) => value.includes(item.value)), [items, value]);

  const selectables = React.useMemo(() => items.filter((item) => !value.includes(item.value) && item.label.toLowerCase().includes(inputValue.toLowerCase())), [items, value, inputValue]);

  const handleUnselect = React.useCallback(
    (item: TagInputItem) => {
      onValueChange(value.filter((v) => v !== item.value));
    },
    [value, onValueChange]
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current;
      if (input) {
        if (e.key === "Delete" || e.key === "Backspace") {
          if (input.value === "" && value.length > 0) {
            onValueChange(value.slice(0, -1));
          }
        }
        if (e.key === "Escape") {
          input.blur();
        }

        setOpen(true);
      }
    },
    [value, onValueChange]
  );

  return (
    <Command shouldFilter={false} onKeyDown={handleKeyDown} className={cn("overflow-visible bg-transparent", className)}>
      <Popover open={open && !isCreating && !duplicateError && (selectables.length > 0 || !!inputValue)} onOpenChange={setOpen}>
        <div className="flex flex-wrap items-center gap-2">
          {selectedItems.map((item) => (
            <Badge key={item.value} variant="muted" className="bg-transparent border border-input px-3 py-0.5 rounded-md text-base text-foreground whitespace-nowrap h-swivu-input">
              {item.label}
              <button
                className="ml-0.5 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleUnselect(item);
                  }
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={() => handleUnselect(item)}
                disabled={disabled}
              >
                <X className="h-4 w-4 text-current opacity-70 hover:opacity-100" />
              </button>
            </Badge>
          ))}
          {isCreating && (
            <Skeleton className="h-swivu-input w-20 flex items-center justify-center bg-transparent border border-input rounded-md">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </Skeleton>
          )}
          <PopoverAnchor className="relative">
            <CommandPrimitive.Input
              ref={inputRef}
              value={inputValue}
              onValueChange={(val) => {
                setInputValue(val);
                const isDuplicate = items.some((item) => item.label === val && value.includes(item.value));
                setDuplicateError(isDuplicate);
              }}
              onBlur={() => {
                setOpen(false);
                setInputValue("");
                setDuplicateError(false);
              }}
              onFocus={() => setOpen(true)}
              placeholder={placeholder}
              disabled={disabled}
              className={cn(
                "flex rounded-md text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-base border bg-transparent h-swivu-input px-3 py-2",
                duplicateError ? "border-destructive focus-visible:ring-destructive" : "border-input"
              )}
            />
            {duplicateError && <p className="text-xs text-destructive absolute -bottom-6 left-1">Tag already exists</p>}
          </PopoverAnchor>
        </div>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0 z-[--swivu-bryntum-z-index]" onOpenAutoFocus={(e) => e.preventDefault()} align="start" sideOffset={4}>
          <CommandList>
            {selectables.length === 0 && !onCreateOption ? (
              <CommandEmpty>No tags found.</CommandEmpty>
            ) : selectables.length === 0 && inputValue && onCreateOption ? (
              <CommandGroup>
                <CommandItem
                  value={`__create__:${inputValue}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onSelect={() => {
                    onCreateOption(inputValue);
                    setInputValue("");
                  }}
                  className="cursor-pointer"
                >
                  <Plus className="h-4 w-4" />
                  Create &quot;{inputValue}&quot;
                </CommandItem>
              </CommandGroup>
            ) : (
              <CommandGroup className="max-h-64 overflow-auto">
                {selectables.map((item) => (
                  <CommandItem
                    key={item.value}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onSelect={() => {
                      setInputValue("");
                      onValueChange([...value, item.value]);
                    }}
                    className="cursor-pointer"
                  >
                    {item.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </PopoverContent>
      </Popover>
    </Command>
  );
}
